import { Box, Gleam, Icon, IconButton, ZillowLogo } from '@zillow/constellation-10';
import { IconMessageFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserListingConversationWebApiHandler_cachedConversationCounts } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { PathsManifest } from '@zg-rentals/paths-manifest';

import { getNavDisplays } from '../../utils/displays';
import HeaderNavigationClickstreamEvents from '../../utils/clickstream';
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

import AccountMenu from '../VerticalNavigation/AccountMenu';
import AdaptiveAnchor from '../AdaptiveAnchor';
import LogoDesktop from '../Logo/LogoDesktop';
import NotificationCenter from '../NotificationCenter';

import type { Props as HeaderNavigationProps } from '../HeaderNavigation';
import type { User } from '../../types';

export type Props = Partial<HeaderNavigationProps>;

// IA top bar (logo, notifications, messages, account) — relocated from the left sidebar via `topBar`.
const TopNavBar = (props: Props) => {
  const { displays } = getNavDisplays(props);

  const {
    altLogo = true,
    brand = 'zillow',
    reactRouterLink: Link,
    userModel = {} as User,
    escapeAllLinks = false,
    unreadMessageCount: unreadMessageCountOverride,
  } = props;

  const { loggedIn } = userModel;
  const { displayMainMessages, displayMainNotificationCenter } = displays;

  const { data: conversationCounts } = useRentalManagerUserListingConversationWebApiHandler_cachedConversationCounts({
    enabled: Boolean(loggedIn) && displayMainMessages && unreadMessageCountOverride === undefined,
    refetchInterval: 60_000,
    meta: {
      tags: ['messages-cached-conversation-counts'],
      context: { api: 'cachedConversationCounts', details: {} },
    },
  });
  const unreadMessageCount = unreadMessageCountOverride ?? conversationCounts?.unreadCount ?? 0;

  const useAbsolutePaths = !props.useRelativePaths;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, props.host).getAllPaths();
  const { propertiesPage, inboxPage } = paths;

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      }
    }
  };

  const messagesButton = (
    <IconButton asChild emphasis="bare" shape="circle" size="md" title={HeaderTitles.messages} tone="neutral">
      <span>
        <Icon>
          <IconMessageFilled />
        </Icon>
      </span>
    </IconButton>
  );

  return (
    <Box
      data-testid="TopNavBar"
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '72px',
        paddingInline: '400',
        borderBottom: '1px solid token(colors.border.muted)',
        backgroundColor: 'bg.neutral',
      }}
    >
      <AdaptiveAnchor
        path={propertiesPage}
        escape={escapeAllLinks}
        useAbsolutePaths={useAbsolutePaths}
        link={Link}
        title="Return to properties page"
        variant="logo"
      >
        {/* marginInlineStart matches the left nav anchor's inner padding so the logo lines up with the nav icons */}
        <Box css={{ width: altLogo ? '200px' : '154px', height: 'auto', marginInlineStart: '300' }}>
          {altLogo ? <LogoDesktop alternate={true} /> : <ZillowLogo showLegacyLogo={true} />}
        </Box>
      </AdaptiveAnchor>

      <Box
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: '400',
          '& [data-c11n-component="Anchor"]': { borderRadius: '50%' },
        }}
      >
        {loggedIn && displayMainNotificationCenter && <NotificationCenter topBar={true} />}

        {loggedIn && displayMainMessages && (
          <AdaptiveAnchor
            path={inboxPage}
            escape={true}
            useAbsolutePaths={useAbsolutePaths}
            link={Link}
            title={HeaderTitles.messages}
            onClick={() => handleLinkClick(HeaderTitles.messages)}
          >
            {unreadMessageCount > 0 ? (
              <Gleam
                appearance="ellipsis"
                count={unreadMessageCount}
                maxCount={99}
                maxSuffix
                onImpact={false}
                tone="default"
                css={{ right: '0', top: '0' }}
              >
                {messagesButton}
              </Gleam>
            ) : (
              messagesButton
            )}
          </AdaptiveAnchor>
        )}

        {loggedIn && <AccountMenu displays={displays} paths={paths} userModel={userModel} avatarOnly={true} />}
      </Box>
    </Box>
  );
};

export default TopNavBar;
